import React, { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Graph,
  Setting2,
  HamburgerMenu,
  Add,
  Home2,
  Chart,
  People,
  Calendar,
  ClipboardText,
  TicketDiscount,
  Task,
  UserTag,
  Receipt,
  DocumentText,
  ArrowDown2,
  ArrowRight2,
  Logout,
  TrendUp,
  Star1,
  Briefcase,
  Profile2User,
  Building,
  Call,
  Location,
  Setting4,
  TicketStar,
  TaskSquare,
  ChartSquare,
  SecurityUser,
  MoneyRecive,
  FolderOpen,
  ArrangeVerticalCircle,
  Sun1,
  Moon,
  Category2,
} from "iconsax-reactjs";
import { getFilteredNavigation, canAccessSettings } from "../../utils/rbac";
import tokenManager from "../../utils/tokenManager";
import { useTheme } from "../../contexts/ThemeContext";

const Sidebar = () => {
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isAllPagesOpen, setIsAllPagesOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    0: true,
    1: true,
    2: false,
    3: true,
    4: false,
    5: true,
  });
  const [user, setUser] = useState(null);
  const dropdownRef = useRef(null);
  const profileRef = useRef(null);

  // Theme-aware color helpers
  const t = {
    bg: isDarkMode ? "bg-[#0C0E12]" : "bg-white",
    border: isDarkMode ? "border-[#22262F]" : "border-[#E2E4E9]",
    text: isDarkMode ? "text-[#CECFD2]" : "text-[#374151]",
    textMuted: isDarkMode ? "text-[#61656C]" : "text-[#9CA3AF]",
    textHeading: isDarkMode ? "text-[#F7F7F7]" : "text-[#111827]",
    textSubtle: isDarkMode ? "text-[#94979C]" : "text-[#6B7280]",
    hover: isDarkMode ? "hover:bg-[#22262F]" : "hover:bg-[#F3F4F6]",
    active: isDarkMode
      ? "bg-[#22262F] text-[#CECFD2]"
      : "bg-[#EEF2FF] text-[#4F46E5]",
    profileCard: isDarkMode
      ? "bg-[#13161B] border-[#22262F] hover:bg-[#1A1D23] hover:border-[#2A2E37]"
      : "bg-[#F9FAFB] border-[#E2E4E9] hover:bg-[#F3F4F6] hover:border-[#D1D5DB]",
    dropdown: isDarkMode
      ? "bg-[#0C0E12] border-[#22262F]"
      : "bg-white border-[#E2E4E9]",
    dropdownHover: isDarkMode ? "hover:bg-[#22262F]" : "hover:bg-[#F3F4F6]",
    scrollbar: isDarkMode ? "#22262f" : "#D1D5DB",
    scrollbarHover: isDarkMode ? "#2a2e37" : "#9CA3AF",
    iconColor: isDarkMode ? "text-[#61656C]" : "text-[#9CA3AF]",
    activeIconColor: isDarkMode ? "text-[#61656C]" : "text-[#4F46E5]",
    onlineDot: isDarkMode ? "border-[#0C0E12]" : "border-white",
    onlineDotProfile: isDarkMode ? "border-[#13161B]" : "border-[#F9FAFB]",
  };

  useEffect(() => {
    const currentUser = tokenManager.getUser();
    setUser(currentUser);

    const handleUserChange = () => {
      const updatedUser = tokenManager.getUser();
      setUser(updatedUser);
    };

    window.addEventListener("userChanged", handleUserChange);
    return () => window.removeEventListener("userChanged", handleUserChange);
  }, []);

  const handleLogout = async () => {
    try {
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
    setIsProfileDropdownOpen(false);
  };

  const toggleProfileDropdown = () => {
    if (!isCollapsed) {
      setIsProfileDropdownOpen(!isProfileDropdownOpen);
    } else {
      setIsProfileDropdownOpen(true);
    }
  };

  const toggleSection = (sectionIndex) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionIndex]: !prev[sectionIndex],
    }));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        profileRef.current &&
        !profileRef.current.contains(event.target)
      ) {
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isCollapsed) setIsProfileDropdownOpen(false);
  }, [isCollapsed]);

  const allNavSections = [
    {
      title: "HOME",
      items: [{ to: "/dashboard", icon: Home2, label: "Dashboard" }],
    },
    {
      title: "SALES & CUSTOMER MANAGEMENT",
      items: [
        { to: "/leads", icon: TrendUp, label: "Leads" },
        { to: "/my-leads", icon: Star1, label: "My Leads" },
        {
          to: "/cleansed-leads",
          icon: ArrangeVerticalCircle,
          label: "Cleansed Leads",
        },
        { to: "/opportunities", icon: Chart, label: "Opportunities" },
        { to: "/my-opportunities", icon: Star1, label: "My Opportunities" },
        { to: "/appointments", icon: Calendar, label: "Appointments" },
        { to: "/accounts", icon: Building, label: "Accounts" },
        { to: "/contacts", icon: Profile2User, label: "Contacts" },
        { to: "/engagements", icon: TrendUp, label: "Cadence" },
        { to: "/cases", icon: ClipboardText, label: "Cases" },
        { to: "/projects", icon: Briefcase, label: "Projects" },
        { to: "/visits", icon: Location, label: "Visits" },
        { to: "/users", icon: People, label: "Team" },
        { to: "/team-tasks", icon: Task, label: "Team Tasks" },
        { to: "/user-settings", icon: Setting2, label: "Settings" },
      ],
    },
  ];

  const navSections = user ? getFilteredNavigation(allNavSections, user) : [];
  const showSettings = user ? canAccessSettings(user) : false;

  const filteredNavSections = navSections
    .map((section) => {
      if (section.title === "ADMINISTRATION" && !showSettings) {
        return {
          ...section,
          items: section.items.filter((item) => item.label !== "Settings"),
        };
      }
      return section;
    })
    .filter((section) => section.items.length > 0);

  const profileMenuItems = [
    {
      icon: Setting2,
      label: "Account settings",
      onClick: () => {
        navigate("/user-settings");
        setIsProfileDropdownOpen(false);
      },
      show: showSettings,
    },
  ].filter((item) => item.show !== false);

  const accountItems = [
    {
      name: user?.name || "User",
      email: user?.email || "user@example.com",
      initials: user?.name[0] || "U",
      isActive: true,
    },
  ];

  if (!user) {
    return (
      <div
        className={`w-80 ${t.bg} border-r ${t.border} min-h-screen flex items-center justify-center transition-colors duration-300`}
      >
        <div className={t.textMuted}>Loading...</div>
      </div>
    );
  }

  return (
    <div
      className={`${
        isCollapsed ? "w-20" : "w-80"
      } ${t.bg} border-r ${t.border} min-h-screen flex flex-col transition-all duration-500 ease-in-out relative overflow-visible`}
    >
      {/* Logo Section */}
      <div
        className={`p-4 transition-all duration-500 ${isCollapsed ? "px-2" : ""}`}
      >
        <div className="flex justify-center">
          <div className="flex items-center justify-center overflow-hidden">
            <img
              src="/assets/logo.png"
              alt="Grade A"
              className={`h-[50px] transition-all duration-500 ${
                isCollapsed ? "scale-90" : "scale-100"
              }`}
            />
          </div>
        </div>
      </div>

      {/* Toggle Button */}
      <button
        onClick={toggleSidebar}
        className={`rounded-sm px-2.5 transition-all duration-300 z-10 transform hover:scale-105 mx-5 mb-4`}
        title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        <HamburgerMenu
          className={`h-5 w-5 transition-transform duration-300 ${
            isCollapsed ? "rotate-180" : ""
          } ${isDarkMode ? "text-white" : "text-[#374151]"}`}
        />
      </button>

      {/* Main Navigation with Sections */}
      <nav className="flex-1 overflow-y-auto px-5 custom-scrollbar">
        {filteredNavSections.map((section, sectionIndex) => {
          const hasMultipleItems = section.items.length > 1;
          const isExpanded = expandedSections[sectionIndex];

          return (
            <div key={sectionIndex} className="mb-6">
              {/* Section Title */}
              {!isCollapsed && (
                <div
                  className={`px-2 mb-2 flex items-center justify-between ${
                    hasMultipleItems ? "cursor-pointer" : ""
                  }`}
                  onClick={() =>
                    hasMultipleItems && toggleSection(sectionIndex)
                  }
                >
                  <h3
                    className={`text-xs font-semibold ${t.textMuted} uppercase tracking-wider`}
                  >
                    {section.title}
                  </h3>
                  {hasMultipleItems && (
                    <div className="transition-transform duration-200">
                      {isExpanded ? (
                        <ArrowDown2 className={`w-3.5 h-3.5 ${t.textMuted}`} />
                      ) : (
                        <ArrowRight2 className={`w-3.5 h-3.5 ${t.textMuted}`} />
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Section Items */}
              <div
                className={`space-y-1 transition-all duration-300 ease-in-out overflow-hidden ${
                  !isCollapsed && hasMultipleItems && !isExpanded
                    ? "max-h-0 opacity-0"
                    : "max-h-[1000px] opacity-100"
                }`}
              >
                {section.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `flex items-center ${
                        isCollapsed ? "justify-center px-2" : "px-4"
                      } py-3 ${
                        isActive
                          ? `${t.active} font-medium rounded-md`
                          : `${t.text} ${t.hover}`
                      } transition-all duration-300 ease-in-out rounded-md`
                    }
                    title={isCollapsed ? item.label : ""}
                  >
                    {({ isActive }) => (
                      <>
                        <div
                          className={`flex items-center justify-center ${
                            isCollapsed ? "w-5" : "w-5 mr-3"
                          } transition-all duration-300`}
                        >
                          <item.icon
                            className={`h-5 w-5 transition-all duration-300 ${
                              isActive ? t.activeIconColor : t.iconColor
                            }`}
                          />
                        </div>

                        <div
                          className={`overflow-hidden transition-all duration-500 ease-in-out ${
                            isCollapsed ? "w-0 opacity-0" : "w-auto opacity-100"
                          }`}
                        >
                          <span className="whitespace-nowrap text-sm">
                            {item.label}
                          </span>
                        </div>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>

      {/* All Pages Button */}
      <div className={`px-5 pb-1`}>
        <button
          onClick={() => {
            navigate("/all-pages");
          }}
          title="All Pages"
          className={`w-full flex items-center ${
            isCollapsed ? "justify-center px-2" : "px-4"
          } py-2.5 rounded-md transition-all duration-300 ${t.hover} ${t.text}`}
        >
          <div
            className={`flex items-center justify-center ${
              isCollapsed ? "w-5" : "w-5 mr-3"
            } transition-all duration-300`}
          >
            <Category2 className={`h-5 w-5 ${t.iconColor}`} />
          </div>
          <div
            className={`overflow-hidden transition-all duration-500 ease-in-out ${
              isCollapsed ? "w-0 opacity-0" : "w-auto opacity-100"
            }`}
          >
            <span className="whitespace-nowrap text-sm">All Pages</span>
          </div>
        </button>
      </div>

      {/* Theme Toggle */}
      <div className={`px-5 pb-3`}>
        <button
          onClick={toggleTheme}
          title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
          className={`w-full flex items-center ${
            isCollapsed ? "justify-center px-2" : "px-4"
          } py-2.5 rounded-md transition-all duration-300 ${t.hover} ${t.text}`}
        >
          <div
            className={`flex items-center justify-center ${
              isCollapsed ? "w-5" : "w-5 mr-3"
            } transition-all duration-300`}
          >
            {isDarkMode ? (
              <Sun1 className={`h-5 w-5 ${t.iconColor}`} />
            ) : (
              <Moon className={`h-5 w-5 ${t.iconColor}`} />
            )}
          </div>
          <div
            className={`overflow-hidden transition-all duration-500 ease-in-out ${
              isCollapsed ? "w-0 opacity-0" : "w-auto opacity-100"
            }`}
          >
            <span className="whitespace-nowrap text-sm">
              {isDarkMode ? "Light mode" : "Dark mode"}
            </span>
          </div>
        </button>
      </div>

      {/* Bottom Profile Section */}
      <div className="mt-auto relative">
        <div
          className={`p-4 border-t ${t.border} transition-all duration-500 ${
            isCollapsed ? "px-2" : ""
          }`}
        >
          <div className="relative">
            {isCollapsed ? (
              <div
                ref={profileRef}
                onClick={toggleProfileDropdown}
                className="flex justify-center"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center cursor-pointer hover:opacity-80 transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
                  {user?.profileImageUrl ? (
                    <img
                      src={user.profileImageUrl}
                      alt={user.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-sm font-medium text-white">
                      {accountItems[0].initials}
                    </span>
                  )}
                  <div
                    className={`absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 ${t.onlineDot} rounded-full`}
                  ></div>
                </div>
              </div>
            ) : (
              <div
                ref={profileRef}
                onClick={toggleProfileDropdown}
                className={`flex items-center ${t.profileCard} rounded-xl border p-3 transition-all duration-300 cursor-pointer`}
              >
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center mr-3 transition-all duration-300 overflow-hidden">
                    {user?.profileImageUrl ? (
                      <img
                        src={user.profileImageUrl}
                        alt={user.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-sm font-medium text-white">
                        {accountItems[0].initials}
                      </span>
                    )}
                  </div>
                  <div
                    className={`absolute bottom-0 right-2 w-3 h-3 bg-green-500 border-2 ${t.onlineDotProfile} rounded-full`}
                  ></div>
                </div>

                <div
                  className={`flex-1 min-w-0 overflow-hidden transition-all duration-500 ease-in-out ${
                    isCollapsed ? "w-0 opacity-0" : "w-auto opacity-100"
                  }`}
                >
                  <div
                    className={`text-sm font-medium ${t.textHeading} truncate whitespace-nowrap`}
                  >
                    {accountItems[0].name}
                  </div>
                  <div
                    className={`text-xs ${t.textSubtle} truncate whitespace-nowrap`}
                  >
                    {accountItems[0].email}
                  </div>
                </div>
              </div>
            )}

            {/* Profile Dropdown Menu */}
            {isProfileDropdownOpen && (
              <div
                ref={dropdownRef}
                className={`absolute ${
                  isCollapsed ? "left-full ml-2" : "left-0 right-0"
                } bottom-0 ${t.dropdown} border rounded-xl shadow-2xl overflow-hidden z-50 animate-in slide-in-from-bottom-2 duration-200 ${
                  isCollapsed ? "w-64" : "w-full"
                }`}
              >
                {/* Menu Items */}
                {profileMenuItems.length > 0 && (
                  <>
                    <div className="py-2 px-2">
                      {profileMenuItems.map((item, index) => (
                        <button
                          key={index}
                          onClick={item.onClick}
                          className={`w-full flex items-center px-4 py-2.5 text-sm ${t.text} ${t.dropdownHover} rounded-lg transition-colors duration-200`}
                        >
                          <item.icon
                            className={`w-4 h-4 mr-3 ${t.iconColor}`}
                          />
                          {item.label}
                        </button>
                      ))}
                    </div>
                    <div className={`border-t ${t.border} my-2`}></div>
                  </>
                )}

                {/* Sign Out */}
                <div className="py-2 px-2">
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsProfileDropdownOpen(false);
                    }}
                    className={`w-full flex items-center px-4 py-2.5 text-sm ${t.text} ${t.dropdownHover} rounded-lg transition-colors duration-200`}
                  >
                    <Logout className={`w-4 h-4 mr-3 ${t.iconColor}`} />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Custom Scrollbar Styles */}
      <style jsx>{`
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: ${t.scrollbar} transparent;
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
          border-radius: 3px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: ${t.scrollbar};
          border-radius: 3px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: ${t.scrollbarHover};
        }
      `}</style>
    </div>
  );
};

export default Sidebar;
